import { useEffect, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { FirebaseError } from "firebase/app";
import App from "../../App";
import { CircleLoader, Button, Message } from "../components";
import { useAppDispatch, setSite, type SiteState } from "../../store";
import {
  getSiteConfig,
  getSiteStatus,
  timestampToDate,
  isoToTimestamp,
  type SiteConfigResponse,
} from "../../services";
import {
  readSlug,
  applyColorScheme,
  applyFontScheme,
  readBakedConfig,
  resolveDisplayName,
  type BakedConfig,
} from "../../utils";
import "./Bootstrap.scss";
import { initI18n } from "../../config/i18n";
import { fetchTexts } from "../../services/textsService";
import { I18nextProvider } from "react-i18next";
import { i18nInstance } from "../../config/i18n";

type BootstrapState = "loading" | "ok" | "missing" | "archived" | "expired" | "error" | "appcheck";

/** How many times to silently retry on App Check permission-denied before giving up. */
const APP_CHECK_MAX_RETRIES = 3;
/** Base delay in ms between App Check retries (doubles each attempt). */
const APP_CHECK_RETRY_BASE_MS = 1500;

const FullScreen = ({ children }: { children: React.ReactNode }) => (
  <div className="bootstrap-screen">
    <div className="bootstrap-card">{children}</div>
  </div>
);

const FullScreenSpinner = () => (
  <div className="bootstrap-screen">
    <CircleLoader />
  </div>
);

const NotFoundScreen = () => (
  <FullScreen>
    <h1>Page not found</h1>
    <h3>This upload page is not available.</h3>
  </FullScreen>
);

const ArchivedScreen = ({ name }: { name: string }) => (
  <FullScreen>
    <h1>{name}</h1>
    <Message variant="warning">This upload window has been closed.</Message>
  </FullScreen>
);

const ExpiredScreen = ({ name, expiresAt }: { name: string; expiresAt: Date }) => (
  <FullScreen>
    <h1>{name}</h1>
    <Message variant="warning">
      This upload window expired on{" "}
      {new Intl.DateTimeFormat("en-US", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }).format(expiresAt)}
      .
    </Message>
  </FullScreen>
);

const ErrorScreen = ({ onRetry }: { onRetry: () => void }) => (
  <FullScreen>
    <h1>Something went wrong</h1>
    <h3>Check your connection and try again.</h3>
    <Button onClick={onRetry}>Try again</Button>
  </FullScreen>
);

const computeBootstrapState = (res: SiteConfigResponse): BootstrapState => {
  if (res.archived) return "archived";
  const expiresAt = timestampToDate(res.expiresAt);
  if (expiresAt && expiresAt.getTime() < Date.now()) return "expired";
  return "ok";
};

interface BakedBootstrapDeps {
  dispatch: ReturnType<typeof useAppDispatch>;
  isCancelled: () => boolean;
  setLocalSite: Dispatch<SetStateAction<SiteConfigResponse | null>>;
  setI18nReady: Dispatch<SetStateAction<boolean>>;
  setState: Dispatch<SetStateAction<BootstrapState>>;
}

/**
 * Bootstraps a PROD build from the injected `window.__CONFIG__` snapshot:
 * paints branding + theme + texts immediately (no presentation fetch),
 * then runs a lightweight runtime archive/expiry gate so takedowns and
 * expirations still apply without rebuilding the bundle.
 */
const bootstrapFromBaked = (
  baked: BakedConfig,
  deps: BakedBootstrapDeps,
): void => {
  const { dispatch, isCancelled, setLocalSite, setI18nReady, setState } = deps;

  document.title = resolveDisplayName(baked.displayName, baked.name);
  document.documentElement.lang = baked.config.language;
  applyColorScheme(baked.config.colorScheme?.palette);
  applyFontScheme(baked.config.fontScheme);

  const synthetic: SiteConfigResponse = {
    siteId: baked.siteId,
    name: baked.name,
    slug: baked.slug,
    displayName: baked.displayName,
    archived: false,
    expiresAt: isoToTimestamp(baked.expiresAt),
    config: {
      language: baked.config.language,
      ...(baked.config.colorScheme
        ? { colorScheme: baked.config.colorScheme }
        : {}),
      ...(baked.config.permissions
        ? { permissions: baked.config.permissions }
        : {}),
      ...(baked.config.timeline ? { timeline: baked.config.timeline } : {}),
      ...(baked.config.menu ? { menu: baked.config.menu } : {}),
    },
  };
  setLocalSite(synthetic);

  const siteState: SiteState = {
    siteId: synthetic.siteId,
    name: synthetic.name,
    displayName: synthetic.displayName,
    slug: synthetic.slug,
    archived: synthetic.archived,
    expiresAt: synthetic.expiresAt,
    config: synthetic.config,
  };
  dispatch(setSite(siteState));

  initI18n(baked.texts.language, baked.texts.keys)
    .catch(() => undefined)
    .finally(() => {
      if (isCancelled()) return;
      setI18nReady(true);
      setState("ok");
    });

  // Authoritative archive/expiry gate. Runs after the snapshot has
  // already painted; failures are non-fatal (keep showing the content).
  getSiteStatus(baked.slug)
    .then((status) => {
      if (isCancelled()) return;
      if (status.archived) {
        setLocalSite((c) => (c ? { ...c, name: status.name, archived: true } : c));
        setState("archived");
        return;
      }
      const expiresAt = timestampToDate(status.expiresAt);
      if (expiresAt && expiresAt.getTime() < Date.now()) {
        setLocalSite((c) => (c ? { ...c, expiresAt: status.expiresAt } : c));
        setState("expired");
      }
    })
    .catch(() => undefined);
};

export const AppBootstrap = () => {
  const dispatch = useAppDispatch();
  const [state, setState] = useState<BootstrapState>("loading");
  const [site, setLocalSite] = useState<SiteConfigResponse | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [appCheckRetry, setAppCheckRetry] = useState(0);
  const [i18nReady, setI18nReady] = useState(false);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    setState("loading");

    // PROD (baked) build: render instantly from the injected snapshot
    // with zero runtime presentation fetch, then gate archive/expiry.
    const baked = readBakedConfig();
    if (baked) {
      bootstrapFromBaked(baked, {
        dispatch,
        isCancelled: () => cancelled,
        setLocalSite,
        setI18nReady,
        setState,
      });
      return () => {
        cancelled = true;
      };
    }

    // No baked snapshot. In prod every valid slug is server-rendered by
    // `renderUploader` with a baked `window.__CONFIG__`, so its absence means
    // an unknown slug → "missing". The runtime presentation fetch below is a
    // DEV-ONLY path (the CRA dev server serves no snapshot); the `NODE_ENV`
    // guard lets it be stripped from the prod bundle.
    if (process.env.NODE_ENV !== "development") {
      setState("missing");
      return;
    }

    let slug: string;
    try {
      slug = readSlug();
    } catch {
      setState("missing");
      return;
    }

    getSiteConfig(slug)
      .then((res) => {
        if (cancelled) return;
        setLocalSite(res);
        const next = computeBootstrapState(res);

        // Apply page-level branding (title + html lang) regardless of state
        // so even the archived/expired screens show the site's name.
        document.title = resolveDisplayName(res.displayName, res.name);
        if (res.config?.language) {
          document.documentElement.lang = res.config.language;
        }

        // Apply the site's theme palette before the app is revealed so the
        // first themed paint is already correct (no flash of default colors).
        // Falls back to the static defaults in index.scss when unset.
        applyColorScheme(res.config?.colorScheme?.palette);
        applyFontScheme(res.config?.fontScheme);

        if (next === "ok") {
          const siteState: SiteState = {
            siteId: res.siteId,
            name: res.name,
            displayName: res.displayName,
            slug: res.slug,
            archived: res.archived,
            expiresAt: res.expiresAt,
            config: res.config,
          };
          dispatch(setSite(siteState));
        }

        const language = res.config?.language ?? "cs-CZ";
        const overrideId = typeof res.config?.textOverrideId === "string"
          ? res.config.textOverrideId
          : undefined;

        fetchTexts(language, overrideId)
          .then((keys) => initI18n(language, keys))
          .then(() => {
            if (cancelled) return;
            if (next === "ok") {
              const siteState: SiteState = {
                siteId: res.siteId,
                name: res.name,
                displayName: res.displayName,
                slug: res.slug,
                archived: res.archived,
                expiresAt: res.expiresAt,
                config: res.config,
              };
              dispatch(setSite(siteState));
            }
            setI18nReady(true);
            setState(next);
          })
          .catch(() => {
            // No fallback bundle: if texts cannot load, there is no page.
            if (!cancelled) setState("error");
          });
      })
      .catch((e: FirebaseError) => {
        if (cancelled) return;
        // The backend throws `not-found` with a specific message when the slug
        // doesn't exist. The Functions SDK also surfaces `functions/not-found`
        // when the *function itself* can't be reached (wrong region, not
        // deployed, etc.) — we don't want to render the "slug doesn't exist"
        // screen in that case, so we check the message too.
        const code = e?.code;
        const message = (e?.message ?? "").toLowerCase();
        const looksLikeMissingSlug =
          code === "functions/not-found" &&
          (message.includes("slug") || message.includes("site"));

        if (looksLikeMissingSlug) {
          setState("missing");
        } else if (code === "functions/permission-denied" && appCheckRetry < APP_CHECK_MAX_RETRIES) {
          // App Check token may not be ready on first load — retry with back-off.
          const delay = APP_CHECK_RETRY_BASE_MS * 2 ** appCheckRetry;
          // eslint-disable-next-line no-console
          console.warn(`App Check denied (attempt ${appCheckRetry + 1}/${APP_CHECK_MAX_RETRIES}), retrying in ${delay}ms…`);
          retryTimer = setTimeout(() => {
            if (!cancelled) setAppCheckRetry((r) => r + 1);
          }, delay);
        } else {
          // eslint-disable-next-line no-console
          console.error("getSiteConfig failed", e);
          setState(code === "functions/permission-denied" ? "appcheck" : "error");
        }
      });

    return () => {
      cancelled = true;
      if (retryTimer !== null) clearTimeout(retryTimer);
    };
  }, [dispatch, attempt, appCheckRetry]);

  if (state === "loading") return <FullScreenSpinner />;
  if (state === "missing") return <NotFoundScreen />;
  if (state === "archived" && site) return <ArchivedScreen name={site.name} />;
  if (state === "expired" && site) {
    return (
      <ExpiredScreen
        name={site.name}
        expiresAt={timestampToDate(site.expiresAt) ?? new Date()}
      />
    );
  }
  if (state === "appcheck") return (
    <FullScreen>
      <h1>Access denied</h1>
      <h3>
        We couldn't verify your browser (Firebase App Check). Try disabling
        script-blocking extensions and make sure the domain
        {" "}
        <strong>{window.location.hostname}</strong>
        {" "}
        is allowed in your App Check settings.
      </h3>
      <Button onClick={() => { setAppCheckRetry(0); setAttempt((a) => a + 1); }}>Try again</Button>
    </FullScreen>
  );
  if (state === "error") return <ErrorScreen onRetry={() => setAttempt((a) => a + 1)} />;

  if (!i18nReady) return <FullScreenSpinner />;

  return (
    <I18nextProvider i18n={i18nInstance}>
      <div
        className={revealed ? "app-reveal revealed" : "app-reveal"}
        onAnimationEnd={(e) => {
          if (e.target === e.currentTarget) {
            setRevealed(true);
          }
        }}
      >
        <App />
      </div>
    </I18nextProvider>
  );
};
